// ============================================================
// numericInput.js — keeping a half-typed number on screen AND in state.
//
// `<input type="number">` DOES NOT ALWAYS HAND BACK WHAT THE USER TYPED, AND
// WHAT IT DROPS IS ENGINE-SPECIFIC. Its value sanitization algorithm empties the
// field for anything it will not parse, so the component sees "" while the
// characters stay on screen. MEASURED, Chromium 141, a controlled React 18
// input, one keystroke at a time:
//
//     typed "."        -> value ""        badInput=true   (no change event)
//     typed "-"        -> value ""        badInput=true
//     typed "1."       -> value "1"       (the trailing point is stripped)
//     typed ".08"      -> value ".08"     — survives, because React skips the
//                         DOM write while state and node already agree on ""
//
// So the spec's strict "valid floating-point number" grammar (which wants a
// digit before the point) is NOT what Chromium enforces, and a leading-decimal
// entry does survive there. WebKit and Gecko are not obliged to agree, and this
// app is used from an iPad; a field whose contents depend on which engine is
// rendering it is not a field anything should be validated from.
//
// HONEST SCOPE: this was written while chasing a cap rate typed as ".08" that
// would not save, and it did NOT turn out to reproduce that failure — see the
// note in the shared Input. It stands on its own as removing the dependency,
// not as the fix for that report.
//
// So a numeric field is rendered as TEXT with inputMode="decimal" (which keeps
// the numeric keypad on a phone or iPad) and the raw string is passed straight
// through, on every engine — ".08" arrives as ".08", and Number() reads it as
// 0.08 exactly as every caller and the API already expect.
//
// SANITIZING IS WHAT REPLACES type=number's OWN REFUSAL OF LETTERS. It admits
// every in-progress form of a number and nothing else, and it REPAIRS rather
// than REJECTS: ignoring a keystroke would leave the browser showing text the
// component's state does not hold, which is the very split this file exists to
// close.
// ============================================================

// Every prefix of a number, including the ones that are not yet a number:
// "" · "-" · "." · ".0" · "1." · "1.08" · "-3.2" · "1e" · "1e-" · "1.79e6".
// Exponent notation is admitted deliberately — `type="number"` accepts it, so
// dropping it here would silently turn a pasted "1.79E6" into 1.796.
//
// THE GRAMMAR NARROWS TO THE FIELD, because a character a field can never mean
// should not be typeable into it. The default is the widest one and is what the
// shared Input uses; a field whose every valid value is a whole non-negative
// number says so, and then a "." cannot reach it at all. That is not tidiness:
// the document upload's tax year becomes a FOLDER PATH SEGMENT, so a lone "."
// there is a folder named "." in SharePoint — one of the few things a number
// input demonstrably DID discard (measured above), so this has to go on
// preventing it now that the field is plain text.
//
// `signed` refers to the VALUE's sign, so an unsigned decimal still admits
// "1e-3" — the minus is inside the exponent and the number is positive.
const GRAMMARS = {
  'signed-decimal': /^-?\d*(\.\d*)?([eE][+-]?\d*)?$/,
  'unsigned-decimal': /^\d*(\.\d*)?([eE][+-]?\d*)?$/,
  'signed-integer': /^-?\d*$/,
  'unsigned-integer': /^\d*$/,
};

const grammarFor = ({ signed = true, decimal = true } = {}) =>
  GRAMMARS[`${signed ? 'signed' : 'unsigned'}-${decimal ? 'decimal' : 'integer'}`];

/** Is this string something the user could still be part-way through typing? */
export const isNumberInProgress = (s, opts) =>
  grammarFor(opts).test(s == null ? '' : String(s));

/**
 * The longest thing the user could have meant, keeping every character that
 * still leaves a number in progress and dropping the rest.
 *
 *   ".08"      → ".08"      (the case that started this)
 *   "1."       → "1."       — mid-typing, never "1"; deleting their own '.' is worse
 *   "abc"      → ""
 *   "12abc"    → "12"
 *   "$1,200"   → "1200"     — a pasted figure lands as the number it is
 *   "1.79E6"   → "1.79E6"
 *
 * With `{ decimal: false }` a page number or a year keeps only its digits, and
 * with `{ signed: false }` a quantity that cannot be negative keeps no minus.
 */
export function sanitizeNumericInput(raw, opts) {
  const re = grammarFor(opts);
  const s = raw == null ? '' : String(raw);
  if (re.test(s)) return s;
  let out = '';
  for (const ch of s) {
    if (re.test(out + ch)) out += ch;
  }
  return out;
}

// The two narrowed shapes every raw numeric field in the app actually wants,
// named rather than spelled out at twelve call sites where one could drift.
export const WHOLE = { signed: false, decimal: false };   // page, pixel, year, count
export const UNSIGNED = { signed: false };                // MB, dollars — never negative

/**
 * The number a field's text stands for, or null when it does not stand for one
 * yet. NULL IS NOT ZERO — a box holding "-" or "." is unfinished, not worth
 * nothing, and reading it as 0 is how a live preview shows $0 at a property
 * somebody is still describing.
 */
export function numericValue(raw) {
  if (raw === null || raw === undefined || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}
